/**
 * New Query / New Query with AI / Package Explorer with AI.
 *
 * Moved verbatim out of `MainToolbar.tsx` by task B11 — same breadcrumbs, same
 * `track()` names, same context prompts, same tab shapes. The two "with AI"
 * actions are the AI ▾ menu's, not the primary row's, and are handed to
 * `AiToolbarButton` unchanged.
 */
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useWorkspaceStore } from '../../workspace';
import { useAiChatStore } from '../../ai-chat/store/aiChatStore';
import { dismissFirstConnectCoach } from '../../workspace/first-connect-bridge';
import {
  createDefaultPackageExplorerContent,
  findExistingPackageExplorerTab,
} from '../../workspace/package-explorer/types';
import { supportsQueryEditorEngineFilter } from '../model/toolbarEngineFilters';
import type { ToolbarActionContext } from './toolbarActionContext';

export function useToolbarQueryActions(ctx: ToolbarActionContext) {
  const { track, requestContext, addTab, getNextQueryNumber, validDbSelection } = ctx;

  const handleNewQuery = async () => {
    addBreadcrumb('click', 'toolbar:new-query');
    track('toolbar:new_query');
    // Clicking New Query is one of the coach's dismissal paths.
    dismissFirstConnectCoach('new_query');
    const queryNumber = getNextQueryNumber();

    // Try to resolve context (prompts user if needed). The engine filter is
    // the SAME predicate the button's disabled gate reads (S1 Task 1.16), so a
    // connection the Query Editor cannot serve is never offered or resolved.
    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsQueryEditorEngineFilter },
      'New Query'
    );

    // If user cancelled or no context available, create query without connection
    if (!context || !supportsQueryEditorEngineFilter(context.engine)) {
      return addTab({ title: `Query-${queryNumber}`, type: 'query' });
    }

    const databaseForTab =
      validDbSelection?.connectionId === context.connectionId
        ? validDbSelection.database
        : undefined;

    // Use resolved context for the new query tab
    const connectionInfo = {
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      serverHost: context.serverHost,
      database: databaseForTab,
    };

    return addTab({ title: `Query-${queryNumber}`, type: 'query', connectionInfo });
  };

  // "New Query Editor with AI" — opens a new query tab (AI sidebar will auto-open via aiChatStore in Slice 2)
  const handleNewQueryWithAi = async () => {
    addBreadcrumb('click', 'toolbar:new-query-with-ai');
    track('toolbar:new_query_with_ai');
    const queryNumber = getNextQueryNumber();

    const context = await requestContext(
      { requireConnection: true, requireDatabase: false, engineFilter: supportsQueryEditorEngineFilter },
      'New Query with AI'
    );

    if (!context || !supportsQueryEditorEngineFilter(context.engine)) {
      const newTabId = addTab({ title: `Query-${queryNumber}`, type: 'query' });
      queueMicrotask(() => useAiChatStore.getState().setSidebarOpen(newTabId, true));
      return;
    }

    const databaseForTab =
      validDbSelection?.connectionId === context.connectionId
        ? validDbSelection.database
        : undefined;

    const connectionInfo = {
      connectionId: context.connectionId,
      connectionName: context.connectionName,
      serverHost: context.serverHost,
      database: databaseForTab,
    };

    const newTabId = addTab({ title: `Query-${queryNumber}`, type: 'query', connectionInfo });
    queueMicrotask(() => useAiChatStore.getState().setSidebarOpen(newTabId, true));
  };

  // "Package Explorer with AI" — opens (or reuses) a package explorer tab with AI sidebar open
  const handleNewPackageExplorerWithAi = async () => {
    addBreadcrumb('click', 'toolbar:package-explorer-with-ai');
    track('toolbar:package_explorer_with_ai');

    const context = await requestContext(
      { requireConnection: true, requireDatabase: true },
      'AI Package Explorer'
    );
    if (!context) return;

    const { tabs, setActiveTab } = useWorkspaceStore.getState();
    const existing = findExistingPackageExplorerTab(tabs, context.connectionId, context.database ?? '');
    if (existing) {
      setActiveTab(existing.id);
      queueMicrotask(() => useAiChatStore.getState().setSidebarOpen(existing.id, true));
      return;
    }

    const newTabId = addTab({
      title: `Package Explorer — ${context.database ?? ''}`,
      type: 'package-explorer',
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: context.connectionName,
        serverHost: context.serverHost,
        database: context.database,
      },
      content: createDefaultPackageExplorerContent({
        connectionId: context.connectionId,
        database: context.database ?? '',
      }),
    });
    queueMicrotask(() => useAiChatStore.getState().setSidebarOpen(newTabId, true));
  };

  return {
    newQuery: () => void handleNewQuery(),
    newQueryWithAi: handleNewQueryWithAi,
    packageExplorerWithAi: handleNewPackageExplorerWithAi,
  };
}
